/**
 * DEVLOGS-TEMP — the single mandatory sanitisation pass.
 *
 * Applied at the PACKAGE BOUNDARY, never at call sites. A call site that has to
 * remember to redact is a call site that will eventually forget.
 *
 * Descended from the precedents already in this repo:
 *   - pino `redact` paths            artifacts/api-server/src/lib/logger.ts
 *   - the CONTEXT_KEYS allowlist     artifacts/api-server/src/lib/logger.ts
 *   - `sanitizeOtaError`             lib/player-shell/src/ota/ota-health.ts
 *   - "srcHost only, never the signed URL"
 *                                    artifacts/web/src/player/lib/player-debug.ts
 *   - "ids, classifications and counts — never a credential, never a URL"
 *                                    lib/player-shell/src/player-telemetry.ts
 *
 * THE DELIBERATE DEPARTURE: the Sentry bridge uses an allowlist, because a
 * Sentry issue only needs to be *attributable*. Here the entire point is broad
 * diagnostic value, so this is a denylist plus a value-shape scanner — and the
 * scanner is the layer that actually works, because real leaks arrive under
 * innocent keys ({ url: "...?X-Amz-Signature=..." }, { detail: "401 Bearer ey..." }).
 *
 * A denylist is the weaker construct. It is acceptable HERE only because this
 * instrument is dev-only, and because an allowlist would strip exactly the
 * payloads that make it worth building.
 */

export const REDACTED = "«redacted»";
export const DEVLOG_MSG_MAX = 512;
export const DEVLOG_STACK_FRAMES = 8;

export interface SanitizedUrl {
  origin: string;
  path: string;
  /** Query KEY NAMES only, sorted and comma-joined. Values are credentials. */
  q?: string;
  /** Values for the provably-safe key allowlist only. */
  kv?: Record<string, string>;
  /** A fragment was present. Never its content — it carries screen credentials. */
  frag?: true;
  bad?: true;
}

/** Query keys whose values are provably non-secret and high diagnostic value. */
const SAFE_QUERY_KEYS = new Set([
  "page",
  "limit",
  "cursor",
  "channeltype",
  "surface",
  "from",
  "to",
  "at",
  "horizonminutes",
  "syncversion",
  "since",
  "kind",
  "activate",
  "player_debug",
  "devlog",
]);

const SAFE_QUERY_VALUE_MAX = 64;

/** Key names whose values never leave the device. Suffix or exact match. */
const DENY_KEYS = [
  "authorization",
  "cookie",
  "set-cookie",
  "setcookie",
  "proxy-authorization",
  "x-api-key",
  "apikey",
  "token",
  "accesstoken",
  "refreshtoken",
  "sessiontoken",
  "idtoken",
  "jwt",
  "bearer",
  "credential",
  "credentials",
  "screencredential",
  "screen_credential",
  "devicecredential",
  "password",
  "passcode",
  "pin",
  "secret",
  "clientsecret",
  "privatekey",
  "signature",
  "sig",
  "x-amz-signature",
  "dsn",
  "phone",
  "phonenumber",
];

const DENY_KEY_SET = new Set(DENY_KEYS);

function keyIsDenied(key: string): boolean {
  const lower = key.toLowerCase();
  if (DENY_KEY_SET.has(lower)) return true;
  // suffix match catches deviceToken, screenCredential, X-Amz-Signature, ...
  for (const deny of DENY_KEYS) {
    if (deny.length >= 5 && lower.endsWith(deny)) return true;
  }
  return false;
}

// --- value-shape patterns ---------------------------------------------------

const JWT_RE = /\b[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/;
const BEARER_RE = /\b(?:Bearer|Basic)\s+[A-Za-z0-9._~+/=-]{8,}/i;
const VENDOR_KEY_RE = /\b(?:sk|pk|rk|whsec|clerk)_[A-Za-z0-9_-]{8,}/i;
const SIGNED_RE = /(?:X-Amz-Signature|Signature|sig)=[A-Za-z0-9%._-]{8,}/i;
const LONG_B64_RE = /\b[A-Za-z0-9_-]{256,}\b/;
const EMAIL_RE = /\b([A-Za-z0-9._%+-])[A-Za-z0-9._%+-]*@([A-Za-z0-9.-]+\.[A-Za-z]{2,})\b/g;
const URLISH_RE = /^[a-z][a-z0-9+.-]*:\/\//i;

/** A path segment that looks like a token rather than an id. */
const OPAQUE_SEGMENT_RE = /^[A-Za-z0-9_-]{64,}$/;

/**
 * Reduce a URL to origin + path + query key names.
 *
 * Keeping the key names is the point: `q: "X-Amz-Algorithm,X-Amz-Expires,
 * X-Amz-Signature"` tells you this is a presigned R2 URL carrying an expiry —
 * which IS the failure mode being hunted — while the signature itself never
 * leaves the device.
 */
export function sanitizeUrl(raw: unknown): SanitizedUrl | null {
  if (typeof raw !== "string" || raw.length === 0) return null;
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    // Relative URL, or not a URL at all. Keep the path shape, drop any query.
    if (raw.startsWith("/")) {
      const [pathOnly, query] = splitQuery(raw);
      const out: SanitizedUrl = { origin: "", path: sanitizePath(pathOnly) };
      const keys = queryKeys(query);
      if (keys) out.q = keys;
      return out;
    }
    return { origin: "", path: "", bad: true };
  }

  const out: SanitizedUrl = {
    origin: parsed.origin === "null" ? parsed.protocol : parsed.origin,
    path: sanitizePath(parsed.pathname),
  };

  const names: string[] = [];
  const kv: Record<string, string> = {};
  parsed.searchParams.forEach((value, key) => {
    names.push(key);
    if (SAFE_QUERY_KEYS.has(key.toLowerCase())) {
      kv[key] = value.slice(0, SAFE_QUERY_VALUE_MAX);
    }
  });
  if (names.length > 0) out.q = [...new Set(names)].sort().join(",");
  if (Object.keys(kv).length > 0) out.kv = kv;
  // Never the fragment content: the desktop shell passes the screen credential
  // in `#screen_credential=` (artifacts/web/src/player/lib/screen-credential.ts).
  if (parsed.hash.length > 1) out.frag = true;

  return out;
}

function splitQuery(raw: string): [string, string] {
  const idx = raw.indexOf("?");
  if (idx < 0) return [raw, ""];
  return [raw.slice(0, idx), raw.slice(idx + 1)];
}

function queryKeys(query: string): string | undefined {
  if (query.length === 0) return undefined;
  const names = query
    .split("&")
    .map((pair) => pair.split("=")[0] ?? "")
    .filter((name) => name.length > 0);
  if (names.length === 0) return undefined;
  return [...new Set(names)].sort().join(",");
}

function sanitizePath(pathname: string): string {
  if (pathname.length === 0) return "";
  return pathname
    .split("/")
    .map((segment) => {
      if (segment.length === 0) return segment;
      if (JWT_RE.test(segment) || OPAQUE_SEGMENT_RE.test(segment)) return REDACTED;
      return segment;
    })
    .join("/");
}

/**
 * Scan a string for credential shapes regardless of the key it arrived under.
 * This is the layer that does the real work.
 */
export function scrubString(input: string, max = DEVLOG_MSG_MAX): string {
  let out = input;
  if (URLISH_RE.test(out.trim())) {
    const url = sanitizeUrl(out.trim());
    if (url) {
      out = `${url.origin}${url.path}${url.q ? `?{${url.q}}` : ""}`;
    }
  }
  if (SIGNED_RE.test(out)) out = out.replace(new RegExp(SIGNED_RE, "gi"), "«signed»");
  if (JWT_RE.test(out)) out = out.replace(new RegExp(JWT_RE, "g"), "«jwt»");
  if (BEARER_RE.test(out)) out = out.replace(new RegExp(BEARER_RE, "gi"), "«bearer»");
  if (VENDOR_KEY_RE.test(out)) out = out.replace(new RegExp(VENDOR_KEY_RE, "gi"), "«key»");
  if (LONG_B64_RE.test(out)) out = out.replace(new RegExp(LONG_B64_RE, "g"), "«blob»");
  // Domain-preserving: "is this a test account or a real customer" is a genuine
  // diagnostic question and the domain answers it. The local part is the PII.
  out = out.replace(EMAIL_RE, (_m, first: string, domain: string) => `${first}***@${domain}`);
  return truncate(out, max);
}

function truncate(value: string, max: number): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max)}…(+${value.length - max})`;
}

/**
 * First line only, whitespace collapsed, credential shapes scrubbed, truncated.
 * `sanitizeOtaError` generalised — with one difference: error MESSAGES are kept
 * in full up to the cap, because the message is very often the diagnosis.
 */
export function sanitizeMessage(
  raw: unknown,
  max = DEVLOG_MSG_MAX,
): string | null {
  if (raw === null || raw === undefined) return null;
  const text =
    typeof raw === "string"
      ? raw
      : raw instanceof Error
        ? raw.message
        : safeString(raw);
  if (text === null) return null;
  const firstLine = text.split("\n", 1)[0] ?? "";
  const collapsed = firstLine.replace(/\s+/g, " ").trim();
  if (collapsed.length === 0) return null;
  return scrubString(collapsed, max);
}

export function sanitizeStack(
  raw: unknown,
  frames = DEVLOG_STACK_FRAMES,
): string | null {
  if (typeof raw !== "string" || raw.length === 0) return null;
  return raw
    .split("\n")
    .slice(0, frames)
    .map((line) => scrubString(line.trim(), 240))
    .join("\n");
}

function safeString(value: unknown): string | null {
  try {
    return String(value);
  } catch {
    return null;
  }
}

/** Normalise any thrown value into the wire error shape. */
export function sanitizeError(
  raw: unknown,
  withStack: boolean,
): { name: string; message: string; stack?: string } | undefined {
  if (raw === null || raw === undefined) return undefined;
  if (raw instanceof Error) {
    const out: { name: string; message: string; stack?: string } = {
      name: raw.name || "Error",
      message: sanitizeMessage(raw.message) ?? "",
    };
    if (withStack) {
      const stack = sanitizeStack(raw.stack);
      if (stack !== null) out.stack = stack;
    }
    return out;
  }
  const message = sanitizeMessage(raw);
  if (message === null) return undefined;
  return { name: typeof raw === "object" ? "Object" : typeof raw, message };
}

// --- structural sanitiser ---------------------------------------------------

export interface SanitizeOptions {
  maxDepth?: number;
  maxKeys?: number;
  maxArray?: number;
  maxString?: number;
  maxBytes?: number;
}

const DEFAULTS: Required<SanitizeOptions> = {
  maxDepth: 4,
  maxKeys: 48,
  maxArray: 32,
  maxString: 1024,
  maxBytes: 4096,
};

/**
 * Cycle-safe, depth-bounded, key-bounded, byte-bounded. Never throws — a
 * throwing getter on a host object is common, not exotic.
 */
export function sanitizePayload(
  value: unknown,
  options?: SanitizeOptions,
): unknown {
  const opts = { ...DEFAULTS, ...options };
  const seen = new WeakSet<object>();
  const result = walk(value, 0, seen, opts, null);
  return capBytes(result, opts.maxBytes);
}

function walk(
  value: unknown,
  depth: number,
  seen: WeakSet<object>,
  opts: Required<SanitizeOptions>,
  key: string | null,
): unknown {
  if (key !== null && keyIsDenied(key)) return REDACTED;

  if (value === null) return null;
  const kind = typeof value;

  if (kind === "string") return scrubString(value as string, opts.maxString);
  if (kind === "number") return Number.isFinite(value as number) ? value : String(value);
  if (kind === "boolean") return value;
  if (kind === "bigint") return String(value);
  if (kind === "symbol") return "«symbol»";
  if (kind === "function") return "«fn»";
  if (kind === "undefined") return undefined;

  const obj = value as object;
  if (seen.has(obj)) return "«cycle»";

  if (depth >= opts.maxDepth) return "«depth»";

  if (value instanceof Error) return sanitizeError(value, false);
  if (value instanceof Date) return safeCall(() => value.toISOString(), "«date»");
  if (value instanceof RegExp) return String(value);
  if (value instanceof Map) return { __type: "Map", size: value.size };
  if (value instanceof Set) return { __type: "Set", size: value.size };
  if (value instanceof ArrayBuffer) return { __type: "ArrayBuffer", byteLength: value.byteLength };
  if (ArrayBuffer.isView(value)) {
    return { __type: value.constructor?.name ?? "TypedArray", byteLength: value.byteLength };
  }

  seen.add(obj);

  if (Array.isArray(value)) {
    const out: unknown[] = [];
    const limit = Math.min(value.length, opts.maxArray);
    for (let i = 0; i < limit; i += 1) {
      const item = walk(value[i], depth + 1, seen, opts, null);
      out.push(item === undefined ? null : item);
    }
    if (value.length > limit) out.push(`«+${value.length - limit} more»`);
    return out;
  }

  // Host objects that carry a URL-ish field (a URL instance, a media element)
  // collapse to just their URL — expanding one would dump the whole DOM.
  //
  // ONLY host objects. A PLAIN object is a caller payload: collapsing
  // `{url, status, deviceId}` to the url alone would silently discard every
  // other field, which is exactly the diagnostic value this exists to carry.
  // Plain-object url strings are already reduced by scrubString on the way past.
  if (!isPlainObject(obj)) {
    const urlish = readUrlish(value as Record<string, unknown>);
    if (urlish !== null) return urlish;
  }

  const out: Record<string, unknown> = {};
  let count = 0;
  const keys = safeCall(() => Object.keys(obj), [] as string[]);
  for (const k of keys) {
    if (count >= opts.maxKeys) {
      out["__truncated"] = true;
      out["__keys"] = keys.length;
      break;
    }
    const raw = safeCall(() => (obj as Record<string, unknown>)[k], undefined);
    const sanitized = walk(raw, depth + 1, seen, opts, k);
    // undefined => omit the key entirely (never null).
    if (sanitized !== undefined) {
      out[k] = sanitized;
      count += 1;
    }
  }
  return out;
}

function isPlainObject(value: object): boolean {
  const proto = safeCall(() => Object.getPrototypeOf(value) as object | null, null);
  return proto === Object.prototype || proto === null;
}

function readUrlish(value: Record<string, unknown>): SanitizedUrl | null {
  for (const field of ["href", "url", "currentSrc", "src"]) {
    const raw = safeCall(() => value[field], undefined);
    if (typeof raw === "string" && URLISH_RE.test(raw)) {
      return sanitizeUrl(raw);
    }
  }
  return null;
}

function safeCall<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

/**
 * Last-resort byte bound. Truncation is made VISIBLE — a silently shortened
 * value reads as the real value, which is worse than an obvious marker.
 */
function capBytes(value: unknown, maxBytes: number): unknown {
  const encoded = safeCall(() => JSON.stringify(value) ?? "", "");
  if (encoded.length <= maxBytes) return value;
  if (value !== null && typeof value === "object" && !Array.isArray(value)) {
    const entries = Object.entries(value as Record<string, unknown>);
    const kept: Record<string, unknown> = {};
    let used = 0;
    for (const [k, v] of entries) {
      const size = safeCall(() => (JSON.stringify(v) ?? "").length, 0) + k.length + 4;
      if (used + size > maxBytes) break;
      kept[k] = v;
      used += size;
    }
    kept["__truncated"] = true;
    kept["__bytes"] = encoded.length;
    return kept;
  }
  return { __truncated: true, __bytes: encoded.length };
}

// --- self-test corpus -------------------------------------------------------

/**
 * A frozen corpus of known-bad strings. `redact.test.ts` asserts BOTH halves for
 * each: the secret never survives, AND the diagnostic remnant does. A sanitiser
 * that returns REDACTED for everything passes the first half and is useless.
 */
export const REDACTION_CORPUS: ReadonlyArray<{
  readonly name: string;
  readonly input: string;
  /** Substrings that must NOT appear in the sanitized output. */
  readonly forbidden: readonly string[];
  /** Substrings that MUST still appear — the diagnostic value. */
  readonly required: readonly string[];
}> = Object.freeze([
  {
    name: "presigned R2 url",
    input:
      "https://media.omnimedia.app/ws/abc/track.mp3?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Expires=3600&X-Amz-Signature=deadbeefcafebabe0123456789abcdef0123456789abcdef",
    forbidden: ["deadbeefcafebabe"],
    required: ["media.omnimedia.app", "/ws/abc/track.mp3", "X-Amz-Signature"],
  },
  {
    name: "clerk session jwt",
    input: "auth failed for eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJ1c2VyXzEyMyJ9.abcdefghijklmnop",
    forbidden: ["eyJzdWIiOiJ1c2VyXzEyMyJ9"],
    required: ["auth failed"],
  },
  {
    name: "bearer header value",
    input: "401 from POST /api/devices with Bearer sk_live_0123456789abcdefghij",
    forbidden: ["sk_live_0123456789abcdefghij"],
    required: ["401", "/api/devices"],
  },
  {
    name: "screen credential fragment",
    input: "http://127.0.0.1:43117/player#screen_credential=abc123def456ghi789jkl",
    forbidden: ["abc123def456ghi789jkl", "screen_credential"],
    required: ["127.0.0.1:43117", "/player"],
  },
  {
    name: "customer email",
    input: "invite bounced for valerio.azzariti@truesmarttech.com",
    forbidden: ["valerio.azzariti@"],
    required: ["truesmarttech.com", "invite bounced"],
  },
]);
